import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import Tag from "../../models/tag";

interface TagsCellProps {
  currentTags: Tag[];
  allTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
}

const TagsCell: React.FC<TagsCellProps> = ({
  currentTags,
  allTags,
  onTagsChange,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const selectedTagIds = new Set(currentTags.map((t: Tag) => t.id));

  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        showDropdown &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [showDropdown]);

  const handleTagToggle = (tagToToggle: Tag) => {
    let selectedTags: Tag[];

    if (selectedTagIds.has(tagToToggle.id)) {
      // Remove tag
      selectedTags = currentTags.filter((t: Tag) => t.id !== tagToToggle.id);
    } else {
      // Add tag
      selectedTags = [...currentTags, tagToToggle];
    }

    onTagsChange(selectedTags);
  };

  const handleRemoveTag = (tagToRemove: Tag) => {
    const selectedTags = currentTags.filter(
      (t: Tag) => t.id !== tagToRemove.id
    );
    onTagsChange(selectedTags);
  };

  const colors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#FFA07A",
    "#98D8C8",
    "#F7DC6F",
    "#BB8FCE",
    "#85C1E2",
    "#F8B739",
    "#52C4A1",
  ];

  const getTagColor = (tagId: number | undefined) => {
    return colors[(tagId || 0) % colors.length];
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexWrap: "wrap",
        gap: "2px",
        padding: "2px",
        alignItems: "center",
        minHeight: "24px",
        overflow: "visible",
        zIndex: 100,
      }}
    >
      {/* Selected Tags Pills */}
      {currentTags.map((tag: Tag) => (
        <div
          key={tag.id}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "2px",
            padding: "2px 6px",
            borderRadius: "10px",
            backgroundColor: getTagColor(tag.id),
            color: "white",
            fontSize: "11px",
            fontWeight: "500",
            whiteSpace: "nowrap",
            lineHeight: "1",
          }}
        >
          <span>{tag.name}</span>
          <button
            onClick={() => handleRemoveTag(tag)}
            style={{
              background: "none",
              border: "none",
              color: "white",
              cursor: "pointer",
              padding: "0 2px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: "1",
            }}
            title="Remover tag"
          >
            ×
          </button>
        </div>
      ))}

      {/* Add Tags Button */}
      <div
        ref={buttonRef}
        style={{
          position: "relative",
          display: "inline-block",
          overflow: "visible",
          zIndex: 100,
        }}
      >
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          style={{
            padding: "2px 6px",
            borderRadius: "4px",
            border: "1px solid #ddd",
            backgroundColor: "#f5f5f5",
            cursor: "pointer",
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            gap: "3px",
            lineHeight: "1",
          }}
          title="Agregar tag"
        >
          <i className="fa fa-plus" style={{ fontSize: "10px" }}></i> Agregar
        </button>
      </div>

      {/* Dropdown Menu - Rendered via Portal */}
      {showDropdown &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: `${dropdownPos.top}px`,
              left: `${dropdownPos.left}px`,
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              zIndex: 999999,
              maxHeight: "300px",
              overflowY: "auto",
              minWidth: "180px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {allTags.length === 0 ? (
              <div style={{ padding: "8px 12px", color: "#999" }}>
                No hay tags disponibles
              </div>
            ) : (
              allTags.map((tag) => (
                <label
                  key={tag.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 12px",
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                    backgroundColor: selectedTagIds.has(tag.id)
                      ? "#f0f7ff"
                      : "white",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f5f5f5")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = selectedTagIds.has(
                      tag.id
                    )
                      ? "#f0f7ff"
                      : "white")
                  }
                >
                  <input
                    type="checkbox"
                    checked={selectedTagIds.has(tag.id)}
                    onChange={() => handleTagToggle(tag)}
                    style={{
                      marginRight: "8px",
                      cursor: "pointer",
                    }}
                  />
                  <span style={{ fontSize: "13px" }}>{tag.name}</span>
                </label>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default TagsCell;
